import React, { useEffect, useState } from 'react';
import { Radio, Loader2, Calendar, Video, Crown, Users, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { MEMBER_NAV } from '../../constants/nav';
import { fetchMemberSessions, fmtRange } from '../../features/liveTrade/api/liveTrade';
import type { LiveSession } from '../../types';
import { StatusBadge } from '../../components/common/StatusBadge';
import { LIVE } from '../../constants/testIds';

const TYPE_META: Record<string, { label: string; icon: any; cls: string }> = {
  public: { label: 'Public', icon: Globe, cls: 'text-sky-300 bg-sky-400/10 border-sky-400/20' },
  member: { label: 'Member', icon: Users, cls: 'text-cyan-300 bg-cyan-400/10 border-cyan-400/20' },
  vip: { label: 'VIP', icon: Crown, cls: 'text-amber-300 bg-amber-400/10 border-amber-400/20' },
};

export default function MemberLiveTradesPage() {
  const [sessions, setSessions] = useState<LiveSession[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMemberSessions().then(setSessions).finally(() => setLoading(false));
  }, []);

  return (
    <DashboardLayout navItems={MEMBER_NAV} title="Live Trade">
      <div className="max-w-5xl" data-testid={LIVE.memberPage}>
        <div className="mb-6 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
            <Radio className="w-5 h-5 text-cyan-400" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-cyan-400">Jadwal</p>
            <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Sesi Live Trade</h2>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} /></div>
        ) : sessions.length === 0 ? (
          <p className="text-white/40 text-sm bg-white/[0.02] border border-white/10 rounded-xl p-6">Belum ada sesi live trade yang tersedia untuk Anda.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sessions.map((s) => {
              const meta = TYPE_META[s.session_type] || TYPE_META.public;
              const Icon = meta.icon;
              const joinable = s.status !== 'completed' && s.status !== 'cancelled' && !!s.meeting_url;
              return (
                <div key={s.id} data-testid={LIVE.sessionCard} className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border flex items-center gap-1.5 ${meta.cls}`}>
                      <Icon className="w-3 h-3" strokeWidth={2} /> {meta.label}
                    </span>
                    <StatusBadge status={s.status} />
                  </div>
                  <h3 className="text-lg font-medium text-white">{s.title}</h3>
                  <p className="text-sm text-white/50 mt-1 line-clamp-2 flex-1">{s.description}</p>
                  <div className="flex items-center gap-2 text-xs text-white/40 mt-4">
                    <Calendar className="w-4 h-4" strokeWidth={1.5} /> {fmtRange(s.starts_at, s.ends_at)}
                  </div>
                  {s.mentor_name && <p className="text-xs text-white/40 mt-1">Mentor: {s.mentor_name}</p>}
                  <button
                    data-testid={LIVE.joinBtn}
                    disabled={!joinable}
                    onClick={() => {
                      if (s.meeting_url) window.open(s.meeting_url, '_blank');
                      else toast.info('Link meeting belum tersedia');
                    }}
                    className="mt-4 w-full bg-cyan-400 text-[#050914] font-medium px-4 py-2.5 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Video className="w-4 h-4" strokeWidth={2} /> Join Sesi
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
